import React, { useState } from 'react';
import type { FoundationPayload, Filters, LoansPayload, LoanGroups, LoanPosition, LoanBucket } from '../types/biApp';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { LineChart } from '../shared/charts';
import { Crumbs, Card as UiCard, DataTable, Pill, ui, type Col } from '../shared/ui';

function emptyLoanBucket(): LoanBucket {
    return { total: 0, positions: [] };
}

function emptyLoanGroups(): LoanGroups {
    const b = emptyLoanBucket;
    return {
        receivables: { related_party_loans: b(), bank_credits: b(), leasing: b() },
        liabilities: { related_party_loans: b(), bank_credits: b(), leasing: b() },
    };
}

function loanReceivablesTotal(groups: LoanGroups): number {
    return groups.receivables.related_party_loans.total + groups.receivables.bank_credits.total + groups.receivables.leasing.total;
}

function loanLiabilitiesTotal(groups: LoanGroups): number {
    return groups.liabilities.related_party_loans.total + groups.liabilities.bank_credits.total + groups.liabilities.leasing.total;
}

function loanGroupsForCompany(companyId: number, payload: LoansPayload): LoanGroups {
    return payload.company_groups.find((row) => Number(row.company_id) === Number(companyId))?.groups ?? emptyLoanGroups();
}

function LoanAccordion({ title, total, defaultOpen = false, children }: { title: string; total: number; defaultOpen?: boolean; children: React.ReactNode }) {
    return (
        <details className="loan-accordion" open={defaultOpen}>
            <summary>
                <span>{title}</span>
                <strong>{formatMoney(total)}</strong>
            </summary>
            <div className="loan-accordion-body">{children}</div>
        </details>
    );
}

function LoanBucketView({ title, bucket, emptyText = 'Nema pozicija u ovoj kategoriji.', onChange, locale = 'hr' }: {
    title: string;
    bucket: LoanBucket;
    emptyText?: string;
    onChange: (patch: Partial<Filters>) => void;
    locale?: 'hr' | 'en';
}) {
    return (
        <section className="loan-bucket">
            <div className="module-heading compact-heading">
                <h4>{title}</h4>
                <span className="pill">{formatMoney(bucket.total)}</span>
            </div>
            {bucket.positions.length === 0 ? <p className="muted">{emptyText}</p> : null}
            {bucket.positions.length > 0 ? (
                <div className="loan-position-grid">
                    {bucket.positions.map((position) => (
                        <LoanPositionCard
                            key={`${position.company_id}-${position.counterparty_id ?? 'none'}-${position.account_code ?? 'na'}-${position.instrument_type}-${position.source ?? 'movement'}`}
                            position={position}
                            onChange={onChange}
                            locale={locale}
                        />
                    ))}
                </div>
            ) : null}
        </section>
    );
}

function LoanPositionCard({ position, onChange, locale = 'hr' }: { position: LoanPosition; onChange: (patch: Partial<Filters>) => void; locale?: 'hr' | 'en' }) {
    const fromBrutoBilanca = position.source === 'trial_balance_finance';
    const openingLabel = fromBrutoBilanca ? 'Bruto bilanca 31.12.2025' : 'PD-IPO/BB 31.12.2025';

    const body = (
        <>
            <div>
                <p className="eyebrow">{position.company_code}</p>
                <h3>{position.counterparty_name}</h3>
                <p className="muted">Stanje na dan {formatDate(position.as_of, locale)}{fromBrutoBilanca ? ' · iz BB (nema drilldowna pokreta u ovom modulu)' : ''}</p>
            </div>
            <div className="mini-balance-grid">
                <span>{openingLabel} <strong>{formatMoney(position.opening_base)}</strong></span>
                <span>Promjena u periodu <strong>{formatMoney(position.period_change)}</strong></span>
                <span>Stanje pozicije <strong>{formatMoney(position.signed_closing)}</strong></span>
            </div>
            <span className="pill">{formatMoney(position.signed_closing)}</span>
        </>
    );

    if (fromBrutoBilanca) {
        return <div className="loan-position-card loan-position-card--static">{body}</div>;
    }

    return (
        <button
            className="loan-position-card interactive-row"
            type="button"
            onClick={() => onChange({
                loanCompanyId: String(position.company_id),
                counterpartyId: position.counterparty_id ? String(position.counterparty_id) : '',
            })}
        >
            {body}
        </button>
    );
}


export function LoansModule({ payload, filters, foundation, onChange, locale = 'hr' }: { payload: LoansPayload | null; filters: Filters; foundation: FoundationPayload; onChange: (patch: Partial<Filters>) => void; locale?: 'hr' | 'en' }) {
    const [overviewQuery, setOverviewQuery] = useState('');
    const [movementQuery, setMovementQuery] = useState('');
    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    if (!payload) {
        return <section className="card">Učitavam pozajmice i kredite...</section>;
    }
    const totalPages = Math.max(1, Math.ceil(payload.movements.total / payload.movements.limit));
    const showPerCompany = !filters.loanCompanyId && !filters.counterpartyId;
    const selectedPosition = payload.positions.find((position) => String(position.company_id) === filters.loanCompanyId && String(position.counterparty_id ?? '') === filters.counterpartyId);
    const filteredCompanies = payload.companies.filter((company) => matchesQuickSearch([company.code, company.name, company.movements, formatMoney(company.net), company.net], overviewQuery));
    const filteredParties = payload.consolidated.filter((party) => matchesQuickSearch([party.counterparty_name, party.movements, formatMoney(party.net), party.net], overviewQuery));
    const filteredMovements = payload.movements.rows.filter((movement) => matchesQuickSearch([
        movement.company_code,
        movement.counterparty_name,
        movement.movement_date,
        movement.movement_type,
        movement.amount,
        formatMoney(movement.amount),
        movement.partner_name,
        movement.description,
        movement.note,
    ], movementQuery));

    const rf = payload.registered_facilities;
    const rfCount = rf ? rf.bank_credits.length + rf.leasing_contracts.length + rf.loan_book.length : 0;

    const renderLoanBuckets = (groups: LoanGroups) => (
        <>
            <LoanAccordion title="Obveze" total={loanLiabilitiesTotal(groups)}>
                <LoanBucketView title="Pozajmice od povezanih osoba" bucket={groups.liabilities.related_party_loans} onChange={onChange} locale={locale} />
                <LoanBucketView title="Krediti prema bankama" bucket={groups.liabilities.bank_credits} emptyText="Nema importiranih bankovnih kredita iz PD-IPO/izvoda u ovoj kategoriji." onChange={onChange} locale={locale} />
                <LoanBucketView title="Leasing i kratki dio kredita (BB)" bucket={groups.liabilities.leasing} emptyText="Nema pozicija iz bruto bilance (253xx, 21440…)." onChange={onChange} locale={locale} />
            </LoanAccordion>
            <LoanAccordion title="Potraživanja" total={loanReceivablesTotal(groups)}>
                <LoanBucketView title="Pozajmice povezanim osobama" bucket={groups.receivables.related_party_loans} onChange={onChange} locale={locale} />
                <LoanBucketView title="Bankovni krediti (potraživanja)" bucket={groups.receivables.bank_credits} emptyText="Nema bankovnih kreditnih potraživanja u trenutnom importu." onChange={onChange} locale={locale} />
                <LoanBucketView title="Leasing (potraživanja, BB)" bucket={groups.receivables.leasing} emptyText="Nema potraživanja iz leasinga u BB." onChange={onChange} locale={locale} />
            </LoanAccordion>
        </>
    );

    return (
        <section className="module-stack">
            {isHrms ? <Crumbs items={['Dashboard', 'Pozajmice i krediti']} /> : null}
            <section className="card hero-module">
                <div className="module-heading">
                    <div>
                        <p className="eyebrow">Pozajmice i krediti na dan {formatDate(filters.dateTo, locale)}</p>
                    <h2>{formatMoney(payload.balances.closing, locale)}</h2>
                        <p className="muted">Bilancna pozicija pozajmica/kredita. {payload.balances.note}</p>
                    </div>
                    <div className="metric-pair">
                        <span>{locale === 'en' ? 'Opening' : 'Početno'} <strong>{formatMoney(payload.balances.opening, locale)}</strong></span>
                        <span>{locale === 'en' ? 'Closing' : 'Završno'} <strong>{formatMoney(payload.balances.closing, locale)}</strong></span>
                        <span>{locale === 'en' ? 'Position change' : 'Promjena pozicije'} <strong>{formatMoney(payload.balances.change, locale)}</strong></span>
                        <span>{locale === 'en' ? 'Net cash' : 'Neto cash'} <strong>{formatMoney(payload.balances.cash_net ?? payload.summary.net, locale)}</strong></span>
                        <span>{locale === 'en' ? 'Movements' : 'Pokreta'} <strong>{formatNumber(payload.summary.movements, locale)}</strong></span>
                    </div>
                </div>
                <LineChart points={payload.graph.map((point) => ({ date: point.date, net: point.net }))} projection={payload.projection.items.map((item) => ({ date: item.date, net: item.amount }))} />
                {!payload.projection.available ? <p className="muted">Buduća projekcija pozajmica je spremna, ali čeka planirane/ručne projekcijske stavke.</p> : null}
            </section>

            {rf && rfCount > 0 ? (
                <section className="card nested-card">
                    <div className="module-heading">
                        <div>
                            <h3>{locale === 'en' ? 'Registered facilities (admin 122–124)' : 'Registrirani instrumenti (admin 122–124)'}</h3>
                            <p className="muted">{rf.note}</p>
                        </div>
                    </div>
                    <div className="loan-accordion-stack">
                        {rf.bank_credits.length > 0 ? (
                            <details className="loan-accordion" open>
                                <summary>
                                    <span>{locale === 'en' ? 'Bank credits' : 'Bankovni krediti'}</span>
                                    <strong>{rf.bank_credits.length}</strong>
                                </summary>
                                <div className="loan-accordion-body">
                                    <ul className="list dense">
                                        {rf.bank_credits.map((row) => (
                                            <li key={`bc-${String(row.id)}`}>
                                                <strong>{String(row.company_code)}</strong> · {String(row.title)} · {formatMoney(Number(row.principal_amount), locale)}
                                                {row.docdoc_contract_url ? (
                                                    <span className="muted">
                                                        {' '}
                                                        ·{' '}
                                                        <a href={String(row.docdoc_contract_url)} target="_blank" rel="noreferrer">
                                                            DocDoc
                                                        </a>
                                                    </span>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </details>
                        ) : null}
                        {rf.leasing_contracts.length > 0 ? (
                            <details className="loan-accordion" open>
                                <summary>
                                    <span>{locale === 'en' ? 'Leasing contracts' : 'Leasing ugovori'}</span>
                                    <strong>{rf.leasing_contracts.length}</strong>
                                </summary>
                                <div className="loan-accordion-body">
                                    <ul className="list dense">
                                        {rf.leasing_contracts.map((row) => (
                                            <li key={`ls-${String(row.id)}`}>
                                                <strong>{String(row.company_code)}</strong> · {String(row.title)} · {formatMoney(Number(row.principal_amount), locale)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </details>
                        ) : null}
                        {rf.loan_book.length > 0 ? (
                            <details className="loan-accordion" open>
                                <summary>
                                    <span>{locale === 'en' ? 'Loan book (interest ÷365)' : 'Knjiga pozajmica (kamata ÷365)'}</span>
                                    <strong>{rf.loan_book.length}</strong>
                                </summary>
                                <div className="loan-accordion-body">
                                    <ul className="list dense">
                                        {rf.loan_book.map((row) => (
                                            <li key={`lb-${String(row.id)}`}>
                                                <strong>{String(row.company_code)}</strong> · {String(row.title)} · {formatMoney(Number(row.principal_amount), locale)}
                                                {row.annual_interest_rate_pct != null ? (
                                                    <span className="muted">
                                                        {' '}
                                                        · god. {String(row.annual_interest_rate_pct)}% → dnevno{' '}
                                                        {row.daily_interest_rate_pct != null ? `${String(row.daily_interest_rate_pct)}%` : '—'}
                                                    </span>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </details>
                        ) : null}
                    </div>
                </section>
            ) : null}

            <section className="card">
                <div className="module-heading">
                    <div>
                        <h3>Konsolidirano — sve odabrane tvrtke</h3>
                        <p className="muted">Pozitivne pozicije su potraživanja, negativne su obveze. Klik na karticu otvara drilldown (osim pozicija iz bruto bilance).</p>
                    </div>
                    {filters.counterpartyId ? <button className="secondary compact" type="button" onClick={() => onChange({ counterpartyId: '', loanCompanyId: '' })}>Natrag na sve pozicije</button> : null}
                </div>
                <div className="loan-accordion-stack">
                    {renderLoanBuckets(payload.groups)}
                </div>
            </section>

            {showPerCompany ? (
                <section className="card">
                    <div className="module-heading">
                        <div>
                            <h3>Po društvima</h3>
                            <p className="muted">Ispod konsolidiranog bloka: za svaku odabranu tvrtku prvo obveze, zatim potraživanja.</p>
                        </div>
                    </div>
                    <div className="company-module-stack">
                        {filters.companyIds.map((companyId) => {
                            const fc = foundation.companies.find((c) => c.id === companyId);
                            if (!fc) {
                                return null;
                            }
                            const cg = loanGroupsForCompany(companyId, payload);
                            return (
                                <section className="card nested-card company-module-block" key={companyId}>
                                    <h2 className="company-module-title">{fc.code}</h2>
                                    <p className="muted company-module-legal">{fc.legal_name}</p>
                                    <div className="loan-accordion-stack">{renderLoanBuckets(cg)}</div>
                                </section>
                            );
                        })}
                    </div>
                </section>
            ) : null}

            <section className="data-grid">
                <article className="card">
                    <div className="module-heading">
                        <h3>Po tvrtkama</h3>
                        <QuickSearchInput value={overviewQuery} onChange={setOverviewQuery} placeholder="Traži tvrtku ili iznos…" />
                    </div>
                    <div className="list dense">
                        {filteredCompanies.map((company) => (
                            <div className="list-row" key={company.code}>
                                <div>
                                    <strong>{company.code}</strong>
                                    <p className="muted">{company.movements} pokreta</p>
                                </div>
                                <span className="pill">{formatMoney(company.net)}</span>
                            </div>
                        ))}
                    </div>
                </article>
                <article className="card">
                    <div className="module-heading">
                        <h3>Po protustrankama</h3>
                        <QuickSearchInput value={overviewQuery} onChange={setOverviewQuery} placeholder="Traži protustranku ili iznos…" />
                    </div>
                    <div className="list dense">
                        {filteredParties.map((party) => (
                            <button className="list-row interactive-row" key={`${party.counterparty_id}-${party.counterparty_name}`} type="button" onClick={() => onChange({ counterpartyId: party.counterparty_id ? String(party.counterparty_id) : '' })}>
                                <div>
                                    <strong>{party.counterparty_name}</strong>
                                    <p className="muted">{party.movements} pokreta</p>
                                </div>
                                <span className="pill">{formatMoney(party.net)}</span>
                            </button>
                        ))}
                    </div>
                </article>
            </section>

            {filters.counterpartyId ? <section className="card nested-card">
                <div className="module-heading">
                    <div>
                        <h3>Drilldown uplata/isplata</h3>
                        <p className="muted">
                            {selectedPosition ? `${selectedPosition.company_code} · ${selectedPosition.counterparty_name} · ` : ''}
                            {formatDate(filters.dateFrom, locale)} - {formatDate(filters.dateTo, locale)} · {payload.movements.total} ukupno, stranica {filters.page} od {totalPages}; prikazano {filteredMovements.length}
                        </p>
                    </div>
                    <div className="pager">
                        <QuickSearchInput value={movementQuery} onChange={setMovementQuery} placeholder="Traži datum, ime, opis ili iznos…" />
                        <button className="secondary compact" type="button" onClick={() => onChange({ counterpartyId: '', loanCompanyId: '' })}>Natrag na sve pozicije</button>
                        <button className="secondary compact" disabled={filters.page <= 1} type="button" onClick={() => onChange({ page: filters.page - 1 })}>Prethodna</button>
                        <button className="secondary compact" disabled={filters.page >= totalPages} type="button" onClick={() => onChange({ page: filters.page + 1 })}>Sljedeća</button>
                    </div>
                </div>
                {isHrms ? (
                    <UiCard pad={0}>
                        <DataTable
                            cols={[
                                { key: 'date', label: 'Datum', render: (m) => formatDate(m.movement_date, locale) },
                                { key: 'company', label: 'Firma', render: (m) => m.company_code },
                                { key: 'party', label: 'Protustranka', render: (m) => m.counterparty_name },
                                { key: 'type', label: 'Tip', render: (m) => <Pill tone="info">{m.movement_type}</Pill> },
                                { key: 'amount', label: 'Iznos', align: 'right', render: (m) => formatMoney(m.amount) },
                                { key: 'desc', label: 'Opis', render: (m) => <div><strong style={{ color: ui.ink }}>{m.partner_name}</strong><div style={{ color: ui.muted, fontSize: '0.8rem' }}>{m.description ?? m.note}</div></div> },
                            ] as Col<(typeof filteredMovements)[number]>[]}
                            rows={filteredMovements}
                            empty="Nema pokreta za prikaz."
                        />
                    </UiCard>
                ) : (
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Datum</th>
                                <th>Firma</th>
                                <th>Protustranka</th>
                                <th>Tip</th>
                                <th>Iznos</th>
                                <th>Opis</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMovements.map((movement) => (
                                <tr key={movement.id}>
                                    <td>{formatDate(movement.movement_date, locale)}</td>
                                    <td>{movement.company_code}</td>
                                    <td>{movement.counterparty_name}</td>
                                    <td>{movement.movement_type}</td>
                                    <td>{formatMoney(movement.amount)}</td>
                                    <td><strong>{movement.partner_name}</strong><p className="muted">{movement.description ?? movement.note}</p></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                )}
            </section> : null}
        </section>
    );
}

